import React from 'react';
import { listSpecs, getSpec, createSpec } from '@services/scoreSpecs';
import { formatStatus } from '@features/scoreSpec/format';
import { buildDuplicate, canDuplicate, documentsKindOf } from './duplicate';
import { splitSpecId, derivedVersion, fullSpecId } from './naming';
import { validateDuplicateForm } from './validate';
import {
  seedContract,
  contractOverrides,
  contractChangeList,
  contractIsDirty,
  contractSchemaOf,
  exitRawSchema,
  adoptSchema,
} from './contractState';
import { contractProblems, localContractProblems } from './contractProblems';
import { isReCut, withFeedContinuity, continuityNote } from './reCut';

// The whole form's state, in one hook.
//
// Local state, no Redux slice — same call as `useScoreSpecs` and `ScoreBackfill` next door: this
// is one screen's data and nothing else in the app reads it.
//
// Two loads, not one. The LIST endpoint is what fills the picker, and its rows are records whose
// `spec_json` we cannot rely on; the definition being copied has to come from GET /:id. So picking
// a source is a fetch, and the form seeds from the result rather than from the row.
//
// Which is also what lets `/edit-score-spec/:specId` work: the fetch screen sends an id it has
// already read, and `selectSource` runs on it exactly as it would on a dropdown pick. The
// approved-only rule therefore has to live in that function rather than in the picker's options, or
// the URL would be a way around a policy the dropdown enforces.

// How far the submit walks the version forward on a 409 before giving up. A 409 means the id is
// taken, which is normal — the source's next version may well already exist as somebody's draft.
// Twenty-five is generous enough that hitting the ceiling means something else is wrong.
const MAX_VERSION_WALK = 25;

const EMPTY_FORM = { stem: '', prompt: '', description: '' };

export const useDuplicateDraft = (initialSourceId) => {
  const [sources, setSources] = React.useState(null);
  const [sourcesError, setSourcesError] = React.useState(null);

  const [sourceId, setSourceId] = React.useState('');
  const [source, setSource] = React.useState(null);
  const [isLoadingSource, setIsLoadingSource] = React.useState(false);
  // Message plus the fix that applies to it. A deleted source and a source that is simply the wrong
  // status both stop the form, but "reload the list and pick again" is only true of the first.
  const [sourceProblem, setSourceProblem] = React.useState(null);

  const [form, setFormState] = React.useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [created, setCreated] = React.useState(null);
  // The output contract overlay. Null until a source is loaded; `isEditing` false until the operator
  // asks for it, and while it is false the payload is byte-for-byte what it was before this
  // overlay existed.
  const [contractState, setContractState] = React.useState(null);
  // Offered only for a re-cut, and off by default: claiming another spec's feed is not something to
  // do by accident.
  const [keepFeed, setKeepFeed] = React.useState(false);

  const loadSources = React.useCallback(async (cancel) => {
    setSourcesError(null);
    const { specs, error } = await listSpecs({ status: 'approved' });
    if (cancel && cancel.isCanceled) return;
    setSources(specs || []);
    setSourcesError(error);
  }, []);

  React.useEffect(() => {
    const cancel = { isCanceled: false };
    loadSources(cancel);
    return () => {
      cancel.isCanceled = true;
    };
  }, [loadSources]);

  // Picking a source discards the form. It is the only control that does — every other edit is
  // additive — and it has to, because all three fields are seeded FROM the source.
  const selectSource = React.useCallback(async (nextId) => {
    setSourceId(nextId);
    setSource(null);
    setSourceProblem(null);
    setSubmitError(null);
    setCreated(null);
    setFormState(EMPTY_FORM);
    setContractState(null);
    setKeepFeed(false);
    if (!nextId) return;

    setIsLoadingSource(true);
    const { spec, error } = await getSpec(nextId);
    setIsLoadingSource(false);

    if (!spec) {
      // 404 here means the source was deleted mid-session, so the picker is stale too.
      setSourceProblem({
        message: error,
        fix: 'If the source was deleted mid-session the picker is stale — reload the list and pick again.',
      });
      return;
    }
    // Approved only — the same rule the picker's options encode, enforced here as well because a
    // deep link never goes through those options. An UNKNOWN status is not evidence of a bad source
    // (same call as the contract check next door); only a known, non-approved one is.
    if (spec.status && spec.status !== 'approved') {
      setSourceProblem({
        message: `${nextId} is ${formatStatus(spec.status).toLowerCase()}, and this mode copies approved specs only.`,
        fix: 'A duplicate inherits behaviour, and approved is the only status that says anyone has signed off on any. Change this one in the advanced editor instead.',
      });
      return;
    }
    setSource(spec);
    const definition = spec.spec_json || {};
    const { stem } = splitSpecId(definition.score_spec_id || spec.score_spec_id, definition.version);
    setFormState({
      stem,
      prompt: (definition.score_gen || {}).prompt || '',
      description: definition.description || '',
    });
    // Seeded here, once, from the normalized duplicate — never re-derived in a render.
    setContractState(seedContract(definition));
  }, []);

  // The deep link, once. Independent of the picker list on purpose: `selectSource` fetches by id, so
  // a slow list must not hold it up and a list that failed must not veto it.
  const autoSelected = React.useRef(false);
  React.useEffect(() => {
    if (autoSelected.current || !initialSourceId) return;
    autoSelected.current = true;
    selectSource(initialSourceId);
  }, [initialSourceId, selectSource]);

  const setForm = React.useCallback((patch) => setFormState((prev) => ({ ...prev, ...patch })), []);

  const definition = source ? source.spec_json : null;
  const sourceIdentity = React.useMemo(
    () => splitSpecId((definition || {}).score_spec_id || sourceId, (definition || {}).version),
    [definition, sourceId]
  );
  const version = derivedVersion(form.stem, sourceIdentity);
  const specId = fullSpecId(form.stem, version);
  const documentsKind = documentsKindOf(definition);

  const errors = React.useMemo(
    () => (definition ? validateDuplicateForm({ ...form, documentsKind }) : []),
    [definition, form, documentsKind]
  );

  // A copy whose PROMPT is unchanged is the same question, so whatever else moved — the shape, a
  // score, a column — it is the same signal and should keep publishing where it published. Gated on
  // the contract having actually been edited, so picking a source does not immediately ask about
  // feeds when nothing has changed yet.
  const reCut = isReCut({ form, definition, contractDirty: contractIsDirty(contractState) });

  const overrides = React.useMemo(() => {
    const base = contractOverrides(contractState);
    if (!base.outputOverride || !reCut || !keepFeed) return base;
    return {
      ...base,
      outputOverride: {
        ...base.outputOverride,
        scores: withFeedContinuity(base.outputOverride.scores, definition),
      },
    };
  }, [contractState, reCut, keepFeed, definition]);

  // Rebuilt on every keystroke so the rewrite list and the JSON preview are never one edit behind
  // what Submit would send. It is a clone-and-delete over a spec-sized object — cheap enough that
  // memoising it on `form` is the whole optimisation needed.
  const preview = React.useMemo(
    () =>
      definition
        ? buildDuplicate(definition, { ...form, version, ...overrides })
        : { spec: null, changes: [], blocked: null },
    [definition, form, version, overrides]
  );

  // The baseline the contract diff is measured against: what this form WOULD send with the editor
  // closed. Not the raw source — a legacy row's `score_jsonpath` has already been desugared by
  // then, and a problem the desugar fixed must not read as one the operator introduced.
  const baseline = React.useMemo(
    () =>
      definition ? buildDuplicate(definition, { stem: 'seed', version: 1, prompt: '', description: '' }).spec : null,
    [definition]
  );

  const contractIssues = React.useMemo(() => {
    if (!contractState || !contractState.isEditing || !preview.spec) return { blocking: [], advice: [] };
    const fromValidator = contractProblems(preview.spec, baseline);
    const local =
      contractState.kind === 'schema'
        ? localContractProblems(contractState.model, contractSchemaOf(contractState))
        : { blocking: [], advice: [] };
    return {
      blocking: fromValidator.blocking.map((p) => `${p.title} — ${p.explain}`).concat(local.blocking),
      advice: fromValidator.advice.map((p) => `${p.title} — ${p.explain}`).concat(local.advice),
    };
  }, [contractState, preview.spec, baseline]);

  const contractChanges = React.useMemo(() => contractChangeList(contractState), [contractState]);
  const feedChanges = React.useMemo(
    () =>
      reCut && keepFeed && definition
        ? [
            `Kept the source’s feed: each score the source declared carries an explicit feed_name, so the copy publishes where ${definition.score_spec_id} published.`,
          ]
        : [],
    [reCut, keepFeed, definition]
  );

  const changes = React.useMemo(
    () => preview.changes.concat(contractChanges).concat(feedChanges),
    [preview.changes, contractChanges, feedChanges]
  );

  const isBlocked = !!preview.blocked;
  const canSubmit =
    !!definition && !isBlocked && errors.length === 0 && !isSubmitting && contractIssues.blocking.length === 0;

  // Every field / score / column control routes through here: the verb decides, and a refusal
  // leaves the model untouched and puts its reason on screen instead.
  const applyContractEdit = React.useCallback((verb) => {
    setContractState((prev) => {
      if (!prev) return prev;
      const { model, blocked } = verb(prev.model);
      return { ...prev, model, blocked: blocked || null };
    });
  }, []);

  const patchContract = React.useCallback((patch) => {
    setContractState((prev) => (prev ? { ...prev, blocked: null, ...patch } : prev));
  }, []);

  // Toggling OFF discards contract edits by re-seeding from the source — the same construction as
  // picking the source again, so nothing edited can survive into a payload the operator thinks is
  // untouched.
  const toggleContractEditing = React.useCallback(() => {
    setContractState((prev) => {
      if (!prev) return prev;
      if (prev.isEditing) {
        const reseeded = seedContract(definition);
        return reseeded ? { ...reseeded, isEditing: false } : prev;
      }
      return { ...prev, isEditing: true, blocked: null };
    });
  }, [definition]);

  const adoptContractSchema = React.useCallback((schema) => {
    setContractState((prev) => (prev ? { ...prev, ...adoptSchema(prev, schema) } : prev));
  }, []);

  const exitContractRaw = React.useCallback(() => {
    setContractState((prev) => (prev ? { ...prev, blocked: null, ...exitRawSchema(prev) } : prev));
  }, []);

  // 409 = that id is taken. Walk the version forward, rebuilding the body each time — the id and
  // `version` both move, and sending a bumped id with a stale `version` is the exact mismatch that
  // survives create and dies at lock.
  const submit = React.useCallback(async () => {
    if (!definition) return;
    setIsSubmitting(true);
    setSubmitError(null);

    for (let attempt = version; attempt < version + MAX_VERSION_WALK; attempt += 1) {
      const { spec: body } = buildDuplicate(definition, { ...form, version: attempt, ...overrides });
      // eslint-disable-next-line no-await-in-loop
      const res = await createSpec(body);
      if (res.status === 200 && res.spec) {
        setIsSubmitting(false);
        setCreated({ record: res.spec, changes });
        return;
      }
      if (res.status !== 409) {
        setIsSubmitting(false);
        setSubmitError(res.error);
        return;
      }
    }

    setIsSubmitting(false);
    setSubmitError(`No free version for "${form.stem}" — choose a different name.`);
  }, [definition, form, version, overrides, changes]);

  const startAnother = React.useCallback(() => {
    setCreated(null);
    setSubmitError(null);
  }, []);

  return {
    sources,
    isLoadingSources: sources === null,
    sourcesError,
    reloadSources: () => loadSources(),
    canDuplicateSource: canDuplicate,

    sourceId,
    source,
    definition,
    isLoadingSource,
    sourceError: sourceProblem ? sourceProblem.message : null,
    sourceErrorFix: sourceProblem ? sourceProblem.fix : null,
    selectSource,

    form,
    setForm,
    errors,
    documentsKind,
    version,
    specId,
    sourceIdentity,

    preview,
    changes,
    reCut: reCut
      ? {
          keepFeed,
          setKeepFeed,
          note: continuityNote((contractState && contractState.model.scores) || [], definition),
          sourceId: definition.score_spec_id,
        }
      : null,
    contract: contractState
      ? {
          ...contractState,
          schema: contractSchemaOf(contractState),
          isDirty: contractIsDirty(contractState),
          problems: contractIssues,
          changes: contractChanges,
          applyEdit: applyContractEdit,
          patch: patchContract,
          toggleEditing: toggleContractEditing,
          exitRaw: exitContractRaw,
          adoptSchema: adoptContractSchema,
        }
      : null,
    isBlocked,
    canSubmit,
    isSubmitting,
    submitError,
    submit,
    created,
    startAnother,
  };
};
